import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../_config/api";
import {
  FiBookOpen,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiLayers,
  FiCheckCircle,
  FiSave,
  FiX,
} from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import AddSectionForm from "./AddSectionForm";
import AddMoreQuestionsModal from "./QuestionComponent/AddMoreQuestionsModal";
import { FiFileText } from "react-icons/fi";
import JoditEditor from "jodit-react";

export default function SectionAndQuestionManager({ examId, totalSections }) {
  const [sections, setSections] = useState([]);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddSection, setShowAddSection] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [showAddQuestion, setShowAddQuestion] = useState(false);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getExamQuestions", {
        sourceId: examId,
        pageNo: 1,
        size: 10,
      });
      if (!res.error) {
        setSections(
          res.data?.list.sort((a, b) => a.sectionOrder - b.sectionOrder),
        );
      }
    } catch (err) {
      showErrorToast("Failed to fetch sections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, [examId]);

  const handleDeleteQuestion = async (questionId) => {
    try {
      const res = await makeRequestAuth("post", "/admin/deleteQuestion", {
        sectionId: activeSection._id,
        questionId,
      });

      if (res?.error) {
        return showErrorToast(res.message);
      }

      showSuccessToast("Question deleted successfully");
      fetchSections();
    } catch (err) {
      showErrorToast("Delete failed");
    }
  };

  const activeSection = sections[activeSectionIdx];

  return (
    <div className="mt-12 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="flex flex-col md:flex-row min-h-[600px]">
        <div className="w-full md:w-72 border-r border-gray-100 bg-gray-50/50 p-4">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="font-bold text-gray-700 flex items-center gap-2">
              <FiLayers className="text-blue-500" />
              Sections ({totalSections}/{sections.length})
            </h3>

            <button
              onClick={() => {
                if (sections.length >= totalSections) {
                  showErrorToast(`You can add only ${totalSections} sections`);
                  return;
                }
                setShowAddSection(true);
              }}
              disabled={sections.length >= totalSections}
              className={`p-1.5 rounded-lg
        ${
          sections.length >= totalSections
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-blue-500 text-white hover:bg-blue-600"
        }`}
            >
              <FiPlus size={18} />
            </button>
          </div>

          <div className="space-y-2">
            {sections.map((sec, idx) => (
              <button
                key={sec._id}
                onClick={() => setActiveSectionIdx(idx)}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all flex justify-between items-center ${
                  activeSectionIdx === idx
                    ? "bg-white shadow-sm border border-gray-100 text-blue-600 font-semibold"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                <span className="truncate">{sec.sectionName}</span>
                <span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-semibold">
                  {sec.questions?.length || 0} / {sec.totalQuestions}
                </span>
              </button>
            ))}
            {sections.length === 0 && !loading && (
              <p className="text-xs text-gray-400 text-center py-10">
                No sections found
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 p-6 bg-white">
          {activeSection ? (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-gray-50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {activeSection.sectionName}
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Managing {activeSection.questions?.length} questions in this
                    section
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (
                      activeSection.questions.length >=
                      activeSection.totalQuestions
                    ) {
                      showErrorToast(
                        `You can add only ${activeSection.totalQuestions} questions`,
                      );
                      return;
                    }
                    setShowAddQuestion(true);
                  }}
                  disabled={
                    activeSection.questions.length >=
                    activeSection.totalQuestions
                  }
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium shadow-lg
    ${
      activeSection.questions.length >= activeSection.totalQuestions
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-emerald-500 hover:bg-emerald-600 text-white"
    }`}
                >
                  <FiPlus /> Add Question
                </button>
              </div>

              <div className="space-y-4">
                {activeSection.paragraph?.isParagraph &&
                  activeSection.paragraph?.text && (
                    <div className="mb-6 p-6 rounded-2xl bg-blue-50 border border-blue-100">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-2">
                        Passage
                      </div>

                      <div
                        className="prose prose-sm max-w-none text-gray-800"
                        dangerouslySetInnerHTML={{
                          __html: activeSection.paragraph.text,
                        }}
                      />
                    </div>
                  )}
                {activeSection.questions?.map((q, idx) => (
                  <QuestionRow
                    key={idx}
                    question={q}
                    sectionId={activeSection?._id}
                    onEdit={() => setEditQuestion(q)}
                    onDelete={() => handleDeleteQuestion(q._id)}
                  />
                ))}
                {activeSection.questions?.length === 0 && (
                  <div className="text-center py-20 border-2 border-dashed border-gray-100 rounded-2xl">
                    <p className="text-gray-400">
                      This section is empty. Start adding questions.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <FiBookOpen size={48} className="mb-4 opacity-20" />
              <p>Select or create a section to manage questions</p>
            </div>
          )}
        </div>
      </div>
      {showAddSection && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowAddSection(false)}
          />

          <div className="relative z-10 w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[95vh] flex flex-col">
            <button
              onClick={() => setShowAddSection(false)}
              className="absolute top-4 right-4 z-50 p-2 text-gray-400 hover:text-red-500 transition-colors"
            >
              <span className="text-xl">✕</span>
            </button>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <AddSectionForm
                examId={examId}
                onComplete={() => {
                  setShowAddSection(false);
                  fetchSections();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showAddQuestion && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowAddQuestion(false)}
          />

          <div
            className="relative z-10 w-full max-w-4xl bg-white rounded-3xl shadow-2xl
                    overflow-hidden max-h-[95vh] flex flex-col"
          >
            <button
              onClick={() => setShowAddQuestion(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
            >
              ✕
            </button>

            <div className="flex-1 overflow-y-auto">
              <AddMoreQuestionsModal
                sectionId={activeSection._id}
                nextQuestion={activeSection.questions?.length + 1 ?? 1}
                maxQuestions={activeSection.totalQuestions}
                onComplete={() => {
                  setShowAddQuestion(false);
                  fetchSections();
                }}
                onClose={() => setShowAddQuestion(false)}
              />
            </div>
          </div>
        </div>
      )}
      {editQuestion && (
        <EditQuestionModal
          sectionId={activeSection._id}
          question={editQuestion}
          onClose={() => setEditQuestion(null)}
          onSuccess={() => {
            setEditQuestion(null);
            fetchSections();
          }}
        />
      )}
    </div>
  );
}

function QuestionRow({ question, sectionId, onEdit, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [explanations, setExplanations] = useState([{ heading: "", text: "" }]);

  const addStep = () => {
    setExplanations([...explanations, { heading: "", text: "" }]);
  };

  const handleChange = (index, field, value) => {
    const updated = [...explanations];
    updated[index][field] = value;
    setExplanations(updated);
  };
  const saveExplanation = async () => {
    try {
      const payload = {
        sectionId: sectionId,
        questionId: question._id,
        explanation: explanations,
      };

      const res = await makeRequestAuth(
        "post",
        "/admin/addAnswersReason",
        payload,
      );

      if (!res.error) {
        showSuccessToast("Explanation saved successfully");
        setShowExplanationModal(false);
      }
    } catch (error) {
      console.error(error);
      showErrorToast(error?.response?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (showExplanationModal) {
      if (question.explanation && question.explanation.length > 0) {
        setExplanations(question.explanation);
      } else {
        setExplanations([{ heading: "", text: "" }]);
      }
    }
  }, [showExplanationModal, question.explanation]);

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
      <div
        className="flex items-center justify-between p-4 cursor-pointer bg-white"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <span className="w-8 h-8 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg text-xs font-bold">
            {question.questionNo}
          </span>
          <div className="text-gray-700 font-medium max-w-[200px] sm:max-w-md">
            {question.questionType === "paragraph" ? (
              <div
                className="prose prose-sm max-w-none text-gray-700 line-clamp-2"
                dangerouslySetInnerHTML={{
                  __html: question.questionText,
                }}
              />
            ) : (
              <p className="truncate">{question.questionText}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowExplanationModal(true);
            }}
            className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <FiFileText size={16} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <FiEdit2 size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 md:px-12 py-6 bg-slate-50/50 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-px w-8 bg-indigo-200"></span>
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-indigo-400">
                Question Details
              </span>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {question.questionType === "paragraph" ? (
                <div
                  /* Yahan humne custom image handling add ki hai */
                  className="prose prose-slate max-w-none text-gray-700 leading-relaxed 
                       prose-img:rounded-xl prose-img:shadow-md prose-img:mx-auto prose-img:max-h-[400px] prose-img:object-contain"
                  dangerouslySetInnerHTML={{ __html: question.questionText }}
                />
              ) : (
                <div className="flex flex-col gap-4">
                  <p className="text-gray-800 text-lg font-semibold leading-snug">
                    {question.questionText}
                  </p>
                  {/* Agar non-paragraph type me alag se image field hai to */}
                  {question.imageUrl && (
                    <img
                      src={question.imageUrl}
                      className="rounded-xl max-h-[400px] object-contain self-start shadow-sm border border-gray-100"
                      alt="Question visual"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Options Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(question.options).map(([key, text]) => {
              const isCorrect = key === question.correctOption;
              return (
                <div
                  key={key}
                  className={`flex items-start gap-4 p-4 rounded-xl transition-all duration-200 border-2 ${
                    isCorrect
                      ? "bg-emerald-50 border-emerald-500/30 text-emerald-900 shadow-sm shadow-emerald-100"
                      : "bg-white border-transparent hover:border-gray-200 text-gray-600 shadow-sm"
                  }`}
                >
                  <div
                    className={`flex shrink-0 items-center justify-center h-8 w-8 rounded-lg font-bold text-sm mt-0.5 ${
                      isCorrect
                        ? "bg-emerald-500 text-white"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {key}
                  </div>
                  <span className="flex-1 font-medium pt-1">
                    {question.optionsType === "image" ? (
                      <img
                        src={text}
                        alt={`Option ${key}`}
                        className="max-h-[120px] object-contain rounded-lg border border-gray-200 bg-white p-2"
                      />
                    ) : (
                      text
                    )}
                  </span>

                  {isCorrect && (
                    <div className="bg-emerald-500 rounded-full p-1 shrink-0 mt-1">
                      <FiCheckCircle className="text-white w-4 h-4" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showExplanationModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-4">
              Add Step-by-Step Explanation
            </h2>

            <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
              {explanations.map((step, index) => (
                <div key={index} className="border rounded-xl p-3 bg-gray-50">
                  <input
                    type="text"
                    placeholder="Heading (e.g., Step 1)"
                    value={step.heading}
                    onChange={(e) =>
                      handleChange(index, "heading", e.target.value)
                    }
                    className="w-full mb-2 px-3 py-2 border rounded-lg text-sm"
                  />
                  <textarea
                    placeholder="Explanation text..."
                    value={step.text}
                    onChange={(e) =>
                      handleChange(index, "text", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-4">
              <button
                onClick={addStep}
                className="text-indigo-600 text-sm font-medium"
              >
                + Add Step
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowExplanationModal(false)}
                  className="px-4 py-2 text-sm bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>

                <button
                  onClick={saveExplanation}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditQuestionModal({ sectionId, question, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    questionText: question.questionText,
    options: { ...question.options },
    correctOption: question.correctOption,
  });

  const handleUpdate = async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/updateQuestion", {
        sectionId,
        questionNo: question.questionNo,
        questionText: formData.questionText,
        options: formData.options,
        correctOption: formData.correctOption,
      });

      if (res?.error) {
        return showErrorToast(res.message);
      }

      showSuccessToast("Question updated!");
      onSuccess();
    } catch (err) {
      showErrorToast("Update failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-lg font-bold text-gray-800">
            Update Question {question.questionNo}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-400 transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
          <div>
            <label className="text-xs font-bold text-gray-400 uppercase mb-2 block ml-1">
              Question Text
            </label>

            {question.questionType === "paragraph" ? (
              <div className="bg-white rounded-xl border border-gray-100 p-2">
                <JoditEditor
                  value={formData.questionText}
                  onChange={(content) =>
                    setFormData({ ...formData, questionText: content })
                  }
                />
              </div>
            ) : (
              <textarea
                value={formData.questionText}
                onChange={(e) =>
                  setFormData({ ...formData, questionText: e.target.value })
                }
                className="w-full bg-gray-50 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]"
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            <label className="text-xs font-bold text-gray-400 uppercase block ml-1">
              Options & Correct Answer
            </label>
            {Object.keys(formData.options).map((key) => (
              <div key={key} className="flex gap-3 items-center">
                <button
                  onClick={() =>
                    setFormData({ ...formData, correctOption: key })
                  }
                  className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold transition-all ${formData.correctOption === key ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`}
                >
                  {key}
                </button>
                <input
                  value={formData.options[key]}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      options: { ...formData.options, [key]: e.target.value },
                    })
                  }
                  className="flex-1 bg-gray-50 border-none rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-gray-50 bg-gray-50/30">
          <button
            onClick={handleUpdate}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-2xl font-bold shadow-lg shadow-blue-100 flex items-center justify-center gap-2 transition-all"
          >
            <FiSave /> Update Question
          </button>
        </div>
      </div>
    </div>
  );
}
